import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { CartCore } from '@theme/cart-core';

/**
 * Cart line UI only. All cart state goes through CartCore.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors
 * @property {HTMLTableRowElement[]} cartItemRows
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    const row = this.#rowForLine(line);
    const key = row?.dataset?.key;
    if (!key) return;

    this.#busy(true);

    const mutation =
      quantity === 0 ? CartCore.remove(key) : CartCore.setQuantity(key, quantity);

    mutation
      .catch((error) => {
        if (error?.cartError) this.#handleCartError(line, error.cartError);
        else console.error(error);
      })
      .finally(() => this.#busy(false));
  }

  /**
   * @param {number} line
   */
  onLineItemRemove(line) {
    const row = this.#rowForLine(line);
    const key = row?.dataset?.key;
    if (!key) return;

    const marker = cartPerformance.createStartingMarker('clear:user-action');
    this.#busy(true);

    CartCore.remove(key)
      .catch((error) => {
        if (error?.cartError) this.#handleCartError(line, error.cartError);
        else console.error(error);
      })
      .finally(() => {
        this.#busy(false);
        cartPerformance.measureFromMarker(marker);
      });
  }

  /**
   * @param {number} line
   * @param {{ errors: string }} parsedResponseText
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (cartItemError instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
    }
    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  #busy(isBusy) {
    this.classList.toggle('cart-items-disabled', isBusy);
  }

  #lineRows() {
    const rows = this.refs.cartItemRows;
    if (Array.isArray(rows)) return rows;
    if (rows) return [rows];
    return [...this.querySelectorAll('tbody .cart-items__table-row')];
  }

  /**
   * @param {number} line
   */
  #rowForLine(line) {
    const rows = this.#lineRows();
    return rows[line - 1] || rows.find((row) => row.querySelector(`[data-cart-line="${line}"]`));
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
